import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import LaundryServicesList from "@/components/laundry/LaundryServicesList";
import LaundryOrderForm from "@/components/laundry/LaundryOrderForm";
import LaundryOrderTrack from "@/components/laundry/LaundryOrderTrack";
import { useApp } from "@/lib/AppContext";
import SharedHeader from "@/components/SharedHeader";

const TABS_AR = [
  { key: "home", label: "الأسعار", icon: "💰" },
  { key: "order", label: "اطلب", icon: "📦" },
  { key: "track", label: "تتبع", icon: "🔍" },
];
const TABS_EN = [
  { key: "home", label: "Prices", icon: "💰" },
  { key: "order", label: "Order", icon: "📦" },
  { key: "track", label: "Track", icon: "🔍" },
];

export default function LaundryCustomerPage() {
  const { lang } = useApp();
  const isAr = lang === "ar";
  const TABS = isAr ? TABS_AR : TABS_EN;
  const [services, setServices] = useState([]);
  const [tab, setTab] = useState("home");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadServices(); }, []);

  const loadServices = async () => {
    setLoading(true);
    const list = await base44.entities.LaundryService.filter({ is_active: true });
    setServices(list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen bg-background flex flex-col relative overflow-hidden"
      dir={isAr ? "rtl" : "ltr"}
    >
      {/* Animated background */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        
        {/* Floating animated boxes */}
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-gradient-to-bl from-cyan-500/15 to-transparent rounded-full blur-3xl animate-pulse" 
             style={{ animationDuration: '4s' }} />
        <div className="absolute top-1/3 -left-16 w-56 h-56 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-3xl blur-2xl animate-pulse" 
             style={{ animationDuration: '5s', animationDelay: '1s' }} />
        <div className="absolute -bottom-16 right-1/4 w-64 h-64 bg-gradient-to-tl from-purple-500/10 to-transparent rounded-full blur-3xl animate-pulse" 
             style={{ animationDuration: '6s', animationDelay: '2s' }} />
        
        {/* Floating particles */}
        <div className="absolute top-20 left-1/4 w-20 h-20 bg-cyan-400/15 rounded-full blur-xl animate-bounce" 
             style={{ animationDuration: '3s' }} />
        <div className="absolute bottom-40 right-1/3 w-24 h-24 bg-blue-400/10 rounded-full blur-xl animate-bounce" 
             style={{ animationDuration: '4s', animationDelay: '0.5s' }} />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" 
             style={{ 
               backgroundImage: 'linear-gradient(rgba(30, 95, 168, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(30, 95, 168, 0.15) 1px, transparent 1px)',
               backgroundSize: '50px 50px' 
             }} />
      </div>

      <SharedHeader />

      {/* ── Scrollable Content ── */}
      <main className="flex-1 overflow-auto pb-24 pt-16 relative z-10" style={{ maxWidth: 480, margin: "0 auto", width: "100%" }}>
        <div className="px-4 py-4">
          {tab === "home" && <LaundryServicesList services={services} loading={loading} onOrder={() => setTab("order")} lang={lang} />}
          {tab === "order" && <LaundryOrderForm services={services} onSuccess={() => setTab("track")} lang={lang} />}
          {tab === "track" && <LaundryOrderTrack lang={lang} />}
        </div>
      </main>

      {/* ── iOS-style Bottom Tab Bar ── */}
      <nav className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t border-border z-30 safe-bottom">
        <div className="max-w-[480px] mx-auto">
        <div className="flex items-center justify-around px-4 pt-2 pb-3">
          {TABS.map(t => {
            const isActive = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className="flex flex-col items-center gap-1 flex-1 transition-all"
              >
                <div className={`w-12 h-8 flex items-center justify-center rounded-2xl transition-all ${isActive ? "bg-cyan-100 dark:bg-cyan-900/40" : ""}`}>
                  <span className={`text-xl leading-none transition-all ${isActive ? "scale-110" : "opacity-60"}`}>{t.icon}</span>
                </div>
                <span className={`text-[11px] font-semibold transition-all ${isActive ? "text-cyan-600 dark:text-cyan-400 font-black" : "text-muted-foreground"}`}>
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
        </div>
      </nav>
    </div>
  );
}